import { inferRouterOutputs } from '@trpc/server';
import { useMutation } from '@tanstack/react-query';
import { trpc } from './trpc';
import { AppRouter } from '@tagadapay/trpc-types';
import { AsyncFilterOptionsHook } from '@/types/data-table';
import { useToast } from '@/components/ui/use-toast';
import {
  buildStoreExportBundle,
} from '@/app/stores/utils/export-selected-stores';
import {
  processBulkCurrencyUpdate,
  type BulkCurrencyStoreRef,
} from '@/app/stores/utils/bulk-update-currencies';
import { useTRPCClient } from '@/lib/api/trpc-provider';
import { PERMISSIONS, usePerm } from '@/hooks/use-perm';
import { normalizeCurrencyCodes } from '@/lib/currency/currencyOptionsKeys';

// Router output types - updated with full store schema
type OutputRouter = inferRouterOutputs<AppRouter>
export type StoreListOutput = OutputRouter['stores']['list']
export type Store = StoreListOutput['stores'][number]
export type StoreDetails = NonNullable<OutputRouter['stores']['getById']>

// Every stores query is gated on Clerk permissions so members without
// `org:stores:view` never trigger the 403 "Access denied" flood — a
// customer/payments/analytics-only role would otherwise fire stores.list
// from shared table filters, sidebar search, report lookup maps, etc.
function useStoresAccess() {
  const { can } = usePerm();
  return {
    canView: can(PERMISSIONS.STORES_VIEW),
    canManage: can(PERMISSIONS.STORES_MANAGE),
  };
}



// Hook for listing stores with full pagination and filters
export function useStoresList(params: {
  search?: string;
  pagination?: { page: number; pageSize: number };
  sortBy?: { field: "productsCount" | "stores.name" | "stores.createdAt" | "stores.type", direction: 'asc' | 'desc' };
  filters?: any;
}, options?: { enabled?: boolean }) {
  const { canView } = useStoresAccess();
  return trpc.stores.list.useQuery(params, {
    enabled: canView && (options?.enabled ?? true),
    staleTime: 30000, // 30 seconds
  });
}

export const useStoreFilterOptions = (): AsyncFilterOptionsHook => {
  const { data: stores, isLoading: isLoadingStores, error: errorStores } = useStoresList({
    pagination: {
      page: 1,
      pageSize: 100,
    },
  });
  return {
    options: stores?.stores.map((store) => ({ label: store.name, value: store.id })) ?? [],
    loading: isLoadingStores,
    error: errorStores !== null,
  }
}

// Hook for fast store search (sidebar)
export function useStoresSearch(
  params: {
    search?: string;
    limit?: number;
  },
  options?: { enabled?: boolean },
) {
  const { canView } = useStoresAccess();
  return trpc.stores.search.useQuery(params, {
    enabled: canView && (options?.enabled ?? true),
    staleTime: 30000, // 30 seconds
  });
}

// Hook for getting a single store by ID
export function useStore(storeId: string, enabled = true) {
  const { canView } = useStoresAccess();
  return trpc.stores.getById.useQuery({ storeId }, {
    enabled: canView && enabled && !!storeId,
    staleTime: 30000, // 30 seconds
  });
}

// Hook to get store currencies (presentment + charge currencies)
export function useStoreCurrencies(storeId: string) {
  const { data: store, isPending: isLoadingStore, isError: isErrorStore } = useStore(storeId);
  if (!store) {
    return {
      data: [],
      isLoading: isLoadingStore,
      isError: isErrorStore,
    }
  }
  const { presentmentCurrencies = [], chargeCurrencies = [] } = store;
  return {
    data: normalizeCurrencyCodes([...presentmentCurrencies, ...chargeCurrencies]),
    isLoading: isLoadingStore,
    isError: isErrorStore,
  }
}

// Hook for creating a store
export function useCreateStore() {
  const utils = trpc.useUtils();
  return trpc.stores.create.useMutation({
    onSuccess: () => {
      // Invalidate stores list so it refetches when user navigates back
      utils.stores.list.invalidate();
      utils.stores.search.invalidate();
    },
  });
}

// Hook for updating a store
export function useUpdateStore(options?: { silent?: boolean }) {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.stores.update.useMutation({
    onSuccess: () => {
      // Invalidate all store queries
      utils.stores.getById.invalidate();
      utils.stores.list.invalidate();
      utils.stores.search.invalidate();
      if (options?.silent) return;
      toast({
        title: 'Success',
        description: 'Store updated successfully',
      });
    },
    onError: (error) => {
      if (options?.silent) return;
      toast({
        title: 'Error',
      });
    },
  });
}

// Hook for persisting the onboarding niche (vertical) onto a store
export function useUpdateStoreVertical() {
  const utils = trpc.useUtils();
  return trpc.stores.updateVertical.useMutation({
    onSuccess: (_data, variables) => {
      // Scope to the store we just wrote — no need to refetch every store-detail query.
      utils.stores.getById.invalidate({ storeId: variables.storeId });
    },
    onError: (error) => {
      // Silent for the merchant (persisting the niche must never block onboarding), but
      // surfaced so a silently-unsaved niche stays diagnosable.
      console.error('[setup] failed to persist store vertical', error);
    },
  });
}

// Hook for deleting a store
export function useDeleteStore() {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.stores.delete.useMutation({
    onSuccess: () => {
      // Invalidate all store queries
      utils.stores.list.invalidate();
      utils.stores.search.invalidate();
      toast({
        title: 'Success',
        description: 'Store deleted successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete store',
        variant: 'destructive',
      });
    },
  });
}

type ExportableStore = { id: string; name: string };

const fetchStoreTemplates = async (
  stores: ExportableStore[],
  fetchExport: (storeId: string) => Promise<unknown>,
) =>
  Promise.all(
    stores.map(async (store) => ({
      storeId: store.id,
      storeName: store.name,
      template: await fetchExport(store.id),
    })),
  );

// Hook for exporting multiple stores as a JSON template bundle (360°)
export function useExportSelectedStores() {
  const utils = trpc.useUtils();
  return useMutation({
    mutationFn: async (stores: ExportableStore[]) => {
      const entries = await fetchStoreTemplates(stores, (storeId) =>
        utils.stores.export.fetch({ storeId }),
      );
      return buildStoreExportBundle(entries);
    },
  });
}

// Hook for adding presentment currencies to multiple stores
export function useBulkUpdateStoreCurrencies() {
  const utils = trpc.useUtils();
  const client = useTRPCClient();
  return useMutation({
    mutationFn: async (input: {
      stores: BulkCurrencyStoreRef[];
      currenciesToAdd: string[];
    }) =>
      processBulkCurrencyUpdate(input.stores, input.currenciesToAdd, {
        fetchStore: (storeId) => utils.stores.getById.fetch({ storeId }),
        updateStore: (payload) => client.stores.update.mutate(payload),
      }),
    onSuccess: () => {
      utils.stores.getById.invalidate();
      utils.stores.list.invalidate();
      utils.stores.search.invalidate();
    },
  });
}

// Hook for exporting a store
export function useExportStore(storeId: string) {
  const { canView } = useStoresAccess();
  return trpc.stores.export.useQuery({ storeId }, {
    enabled: canView && !!storeId,
    staleTime: 0, // Always fresh for exports
  });
}

// Hook for importing a store
export function useImportStore() {
  return trpc.stores.import.useMutation();
}

// Hook for duplicating a store
export function useDuplicateStore() {
  return trpc.stores.duplicate.useMutation();
}

// Hook for creating a store from template
export function useCreateStoreFromTemplate() {
  return trpc.stores.createFromTemplate.useMutation();
}

// Hook for getting available templates
export function useStoreTemplates() {
  const { canManage } = useStoresAccess();
  return trpc.stores.getTemplates.useQuery(undefined, {
    // Templates are only used in store create/import flows — gate on manage.
    enabled: canManage,
    staleTime: 60000, // 1 minute
  });
}

// Hook for getting template details
export function useStoreTemplateDetails(templateName: string, enabled = true) {
  const { canManage } = useStoresAccess();
  return trpc.stores.getTemplateDetails.useQuery({ templateName }, {
    enabled: canManage && enabled && !!templateName,
    staleTime: 60000, // 1 minute
  });
}

// Hook for updating credit settings
export function useUpdateStoreCreditSettings() {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.stores.updateCreditSettings.useMutation({
    onSuccess: () => {
      utils.stores.getById.invalidate();
      toast({
        title: 'Success',
        description: 'Credit settings updated successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update credit settings',
        variant: 'destructive',
      });
    },
  });
}

// Hook for updating experimental features
export function useUpdateExperimentalFeatures() {
  return trpc.stores.updateExperimentalFeatures.useMutation();
}

// Hook for unselecting payment flow from stores
export function useUnselectPaymentFlow() {
  return trpc.stores.unselectPaymentFlow.useMutation();
}

// Hook for listing stores using payment flow
export function useStoresUsingPaymentFlow(flowId: string, enabled = true) {
  const { canView } = useStoresAccess();
  return trpc.stores.listUsingPaymentFlow.useQuery({ flowId }, {
    enabled: canView && enabled && !!flowId,
    staleTime: 30000, // 30 seconds
  });
}
